import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Container,
  Form,
  Button,
  Spinner,
  Alert,
  Badge,
  ButtonGroup,
} from "react-bootstrap";
import AdminSidebar from "../../../components/admin/sidebar/adminSidebar";
import axiosInstance from "../../../api/axios";

export default function ApprovalRequestDetail() {
  const { approvalRequestId } = useParams();
  const [requestDetail, setRequestDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const navigate = useNavigate();

  const fetchDetail = async () => {
    setLoading(true);
    try {
      const response = await axiosInstance.get(
        `/api/approval-request/detail/${approvalRequestId}`
      );
      setRequestDetail(response.data.data);
    } catch (error) {
      console.error("Error fetching approval request detail:", error);
      setError("결재 요청 상세 정보를 가져오는 중 오류가 발생했습니다.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDetail();
  }, [approvalRequestId]);

  const handleDownload = (attachmentPath) => {
    window.open(attachmentPath, "_blank");
  };

  const decodeFileName = (attachmentPath) => {
    try {
      // 파일 경로에서 파일명 추출 및 디코딩
      const encodedFileName = attachmentPath.split("/").pop();
      return decodeURIComponent(encodedFileName);
    } catch (error) {
      console.error("Error decoding file name:", error);
      return "알 수 없는 파일명";
    }
  };

  const handleStatusChange = async (status) => {
    try {
      await axiosInstance.put(
        `/api/approval-request/status/${approvalRequestId}`,
        { status }
      );
      alert(
        `결재 요청이 ${
          status === "FIRST_APPROVED"
            ? "1차 승인"
            : status === "FINAL_APPROVED"
            ? "최종 승인"
            : "반려"
        }되었습니다.`
      );
      fetchDetail();
    } catch (error) {
      console.error("Error updating approval request status:", error);
      alert("결재 요청 상태를 업데이트하는 중 오류가 발생했습니다.");
    }
  };

  // 사용자의 역할을 받아오는 함수 (API 연동 가능)
  const getUserRole = () => {
    const user = JSON.parse(sessionStorage.getItem("user")); // "user"라는 key로 저장된 데이터 가져오기
    return user ? user.position : null; // user가 존재하면 position 반환, 없으면 null
  };

  const userRole = getUserRole();

  const renderStatusBadge = (status) => {
    switch (status) {
      case "PENDING":
        return <Badge bg="warning">요청</Badge>;
      case "FIRST_APPROVED":
        return <Badge bg="primary">1차 승인</Badge>;
      case "FINAL_APPROVED":
        return <Badge bg="success">최종 승인</Badge>;
      case "REJECTED":
        return <Badge bg="danger">반려</Badge>;
      default:
        return <Badge bg="secondary">알 수 없음</Badge>;
    }
  };

  const getCategory = (category) => {
    switch (category) {
      case "EXPENDITURE":
        return "지출";
      case "EMPLOYMENT":
        return "재직증명서";
      case "OTHER":
        return "기타";
      default:
        return "알 수 없음";
    }
  };

  if (loading) {
    return (
      <AdminSidebar>
        <Container className="my-5 text-center">
          <Spinner animation="border" />
          <p className="mt-3">로딩 중...</p>
        </Container>
      </AdminSidebar>
    );
  }

  if (error) {
    return (
      <AdminSidebar>
        <Container className="my-5 text-center">
          <Alert variant="danger">{error}</Alert>
          <Button onClick={() => navigate(-1)}>이전 페이지로</Button>
        </Container>
      </AdminSidebar>
    );
  }

  return (
    <AdminSidebar>
      <Container className="my-5">
        <h3 className="mb-4 text-center">결재 요청 상세 정보</h3>

        <Form>
          {/* 제목 */}
          <Form.Group controlId="title" className="mb-3">
            <Form.Label>제목</Form.Label>
            <Form.Control type="text" value={requestDetail.title} readOnly />
          </Form.Group>

          {/* 구분 */}
          <Form.Group controlId="category" className="mb-3">
            <Form.Label>구분</Form.Label>
            <Form.Control
              type="text"
              value={getCategory(requestDetail.category)}
              readOnly
            />
          </Form.Group>

          {/* 내용 */}
          <Form.Group controlId="content" className="mb-3">
            <Form.Label>내용</Form.Label>
            <Form.Control
              as="textarea"
              rows={5}
              value={requestDetail.content}
              readOnly
            />
          </Form.Group>

          {/* 상태 */}
          <Form.Group controlId="status" className="mb-3">
            <Form.Label>상태</Form.Label>
            <div>{renderStatusBadge(requestDetail.status)}</div>
          </Form.Group>

          {/* 생성일 */}
          <Form.Group controlId="createdAt" className="mb-3">
            <Form.Label>생성일</Form.Label>
            <Form.Control
              type="text"
              value={new Date(requestDetail.createdAt).toLocaleString("ko-KR", {
                year: "numeric",
                month: "2-digit",
                day: "2-digit",
                hour: "2-digit",
                minute: "2-digit",
                second: "2-digit",
              })}
              readOnly
            />
          </Form.Group>

          {/* 첨부파일 */}
          {requestDetail.attachmentPath && (
            <>
              <Form.Group controlId="attachment" className="mb-3">
                <Form.Label>첨부파일</Form.Label>
                <Form.Control
                  type="text"
                  value={decodeFileName(requestDetail.attachmentPath)}
                  readOnly
                />
              </Form.Group>
              <Button
                variant="primary"
                onClick={() => handleDownload(requestDetail.attachmentPath)}
                className="mb-3"
              >
                다운로드
              </Button>
            </>
          )}

          {/* 승인/반려 버튼 (권한에 따라 보이도록) */}
          <div className="text-center mt-4">
            <ButtonGroup>
              {userRole === "원장" && (
                <Button
                  variant="primary"
                  onClick={() => handleStatusChange("FIRST_APPROVED")}
                >
                  1차 승인 (원장)
                </Button>
              )}
              {userRole === "이사장" && (
                <Button
                  variant="success"
                  onClick={() => handleStatusChange("FINAL_APPROVED")}
                >
                  최종 승인 (이사장)
                </Button>
              )}
              <Button
                variant="danger"
                onClick={() => handleStatusChange("REJECTED")}
              >
                반려
              </Button>
            </ButtonGroup>
          </div>

          <div className="text-center mt-4">
            <Button variant="secondary" onClick={() => navigate(-1)}>
              이전 페이지로
            </Button>
          </div>
        </Form>
      </Container>
    </AdminSidebar>
  );
}
